// API service for Swipey.ai authentication endpoints
import { getRedTrackClickId } from './redtrack'
import { getTrackingParams } from './tracking'
import { setStorage, getStorage, setAuth } from './storage'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`

const API_KEY = process.env.REACT_APP_API_KEY

// Lander identifier sent to the backend so registrations can be attributed to
// this LP, regardless of whether the user signs up via email or Google.
export const LP_SOURCE = 'createaisexfriend'
// Voice label mapping
const voiceLabelMap = {
  Cute: 'Voice 2',
  teen: 'Voice 3',
  Soft: 'Voice 4',
  Dominant: 'Voice 5',
}

// Ethnicity mapping
const ethnicityLabelMap = {
  Arab: 'Mid.Eastern',
}

// Attributes map cache
let attributesMap = {}
let attributesLoaded = false

// Common headers for all API requests
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': API_KEY,
})

// External-click-id keys cache. The list of keys (e.g. ['twclid','gclid','fbclid','ttclid'])
// is owned by the backend; we fetch it once per page load and reuse it for every auth call.
let externalClickIdKeys = null
let externalClickIdKeysPromise = null

// Snapshot the landing-URL query params into sessionStorage on module load.
// We read externalClickIds from this snapshot — NOT from localStorage `utm_data`
// (which merges across visits and leaks stale click-ids), and NOT from
// `window.location.search` at submit time (SPA navigation strips it).
// sessionStorage clears when the tab closes → each tab starts fresh.
const LANDING_URL_PARAMS_KEY = 'landing_url_params'
;(() => {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(LANDING_URL_PARAMS_KEY)) return
    const snapshot = {}
    new URLSearchParams(window.location.search).forEach((value, key) => {
      snapshot[key] = value
    })
    sessionStorage.setItem(LANDING_URL_PARAMS_KEY, JSON.stringify(snapshot))
  } catch {}
})()

// Fetch the list of click-id keys the backend wants us to forward. Cached for the page lifetime;
// in-flight requests are deduped via `externalClickIdKeysPromise`. Failures / timeouts fall back
// to [] so auth + OAuth redirects can't be blocked by this endpoint — externalClickIds gets sent as {}.
const EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS = 3000

const fetchExternalClickIdKeys = async () => {
  if (Array.isArray(externalClickIdKeys)) return externalClickIdKeys
  if (externalClickIdKeysPromise) return externalClickIdKeysPromise

  externalClickIdKeysPromise = (async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS)
      : null
    try {
      const response = await fetch(`${API_BASE_URL}/marketing/external-click-id-keys`, {
        method: 'GET',
        headers: getHeaders(),
        signal: controller?.signal,
      })
      if (!response.ok) {
        externalClickIdKeys = []
        return externalClickIdKeys
      }
      const data = await response.json()
      externalClickIdKeys = Array.isArray(data) ? data : []
      return externalClickIdKeys
    } catch (err) {
      console.warn('[externalClickIds] keys fetch failed, falling back to []', err)
      externalClickIdKeys = []
      return externalClickIdKeys
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      externalClickIdKeysPromise = null
    }
  })()

  return externalClickIdKeysPromise
}

// Build the externalClickIds object the backend expects: for each key returned by
// /marketing/external-click-id-keys, pull the value from this tab's landing-URL snapshot.
// Source rationale (see LANDING_URL_PARAMS_KEY comment above):
//   - NOT localStorage.utm_data → leaks click-ids from previous visits
//   - NOT window.location.search at submit time → SPA navigation strips it
export const getExternalClickIds = async () => {
  const keys = await fetchExternalClickIdKeys()
  let landingParams = {}
  try {
    const raw = sessionStorage.getItem(LANDING_URL_PARAMS_KEY)
    if (raw) landingParams = JSON.parse(raw) || {}
  } catch {
    landingParams = {}
  }

  return keys.reduce((acc, key) => {
    const value = landingParams[key]
    acc[key] = value == null || value === '' ? null : value
    return acc
  }, {})
}

// Generic API request function
const apiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    const config = {
      method,
      headers: getHeaders(),
    }

    if (body) {
      config.body = JSON.stringify(body)
    }
    console.log('API_BASE_URL!!!', API_BASE_URL)
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`)
    }

    return data
  } catch (error) {
    throw error
  }
}

// Load attributes from API
export const loadCharacterAttributes = async () => {
  if (attributesLoaded) return attributesMap

  try {
    const response = await fetch(`${API_BASE_URL}/characters-attributes`, {
      method: 'GET',
      headers: getHeaders(),
    })

    if (!response.ok) {
      throw new Error('❌ Unable to load attributes')
    }

    const result = await response.json()

    result.forEach(attr => {
      if (!attr.type || !attr.label || !attr._id) return
      const type = attr.type.toLowerCase()
      if (!attributesMap[type]) attributesMap[type] = {}

      // Create a key that includes style and gender for better matching
      const key = attr.style && attr.gender ? `${attr.label.trim()}_${attr.style}_${attr.gender}` : attr.label.trim()

      attributesMap[type][key] = attr._id

      // Also store under the original label for backward compatibility
      attributesMap[type][attr.label.trim()] = attr._id
    })

    attributesLoaded = true
    return attributesMap
  } catch (err) {
    return {}
  }
}

// Helper function to get attribute ID with style and gender matching
export const getAttributeId = (type, label, userStyle, userGender) => {
  if (!attributesMap[type] || !label) return null

  // Handle voice mapping
  if (type === 'voice' && voiceLabelMap[label]) {
    label = voiceLabelMap[label]
  }

  // Handle ethnicity mapping
  if (type === 'ethnicity' && ethnicityLabelMap[label]) {
    label = ethnicityLabelMap[label]
  }

  // First try to find with exact style and gender match
  if (userStyle && userGender) {
    const styleKey = `${label.trim()}_${userStyle}_${userGender}`
    if (attributesMap[type][styleKey]) {
      return attributesMap[type][styleKey]
    }
  }

  // Fallback to original label matching
  return attributesMap[type][label.trim()] || null
}

const apiToCamelCaseMap = {
  ethnicity: 'ethnicity',
  age: 'ageAttribute',
  eyecolor: 'eyeColor',
  hairstyle: 'hairStyle',
  haircolor: 'hairColor',
  bodytype: 'bodyType',
  breastsize: 'breastSize',
  buttsize: 'buttSize',
  personality: 'personality',
  occupation: 'occupation',
  hobbies: 'hobbies',
  relationship: 'relationship',
  voice: 'defaultVoice',
}

// Convert quiz selections to API format
export const convertSelectionsToApiFormat = async selections => {
  // Ensure attributes are loaded
  await loadCharacterAttributes()

  const userAnswers = {
    gender: 'Girls',
    style: selections?.welcome === 'Anime' ? 'Anime' : 'Realistic',
    attributes: {},
  }

  const mapAgeToApiLabel = age => {
    if (age >= 18 && age <= 19) return '18+'
    if (age >= 20 && age <= 29) return '20s'
    if (age >= 30 && age <= 39) return '30s'
    return '40+'
  }

  // Step1: ethnicity, age
  if (selections?.step1) {
    if (selections.step1.ethnicity) {
      const id = getAttributeId('ethnicity', selections.step1.ethnicity)
      if (id) userAnswers.attributes[apiToCamelCaseMap.ethnicity] = id
    }
    if (selections.step1.age) {
      const ageId = getAttributeId('age', mapAgeToApiLabel(selections.step1.age))
      if (ageId) userAnswers.attributes[apiToCamelCaseMap.age] = ageId
    }
  }

  // Step2: body attributes
  if (selections?.step2) {
    const step2Map = {
      bodyType: 'bodytype',
      breastSize: 'breastsize',
      buttSize: 'buttsize',
    }
    Object.entries(step2Map).forEach(([key, apiKey]) => {
      if (selections.step2[key]) {
        const id = getAttributeId(apiKey, selections.step2[key])
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step3: hair, eyes
  if (selections?.step3) {
    const step3Map = {
      hairColor: 'haircolor',
      hairStyle: 'hairstyle',
      eyeColor: 'eyecolor',
    }
    Object.entries(step3Map).forEach(([key, apiKey]) => {
      if (selections.step3[key]) {
        const id = getAttributeId(apiKey, selections.step3[key])
        if (id) userAnswers.attributes[apiToCamelCaseMap[apiKey]] = id
      }
    })
  }

  // Step4: personality, voice
  if (selections?.step4) {
    if (selections.step4.personality) {
      const ids = Array.isArray(selections.step4.personality)
        ? selections.step4.personality.map(p => getAttributeId('relationship', p)).filter(Boolean)
        : [getAttributeId('relationship', selections.step4.personality)].filter(Boolean)

      if (ids.length > 0) userAnswers.attributes[apiToCamelCaseMap.relationship] = ids.length > 1 ? ids : ids[0]
    }

    if (selections.step4.voice) {
      const voiceId = getAttributeId('voice', selections.step4.voice)
      if (voiceId) userAnswers.attributes[apiToCamelCaseMap.voice] = voiceId
    }
  }

  return userAnswers
}

// Create model-params from selections
export const createModelParams = async selections => {
  const userAnswers = await convertSelectionsToApiFormat(selections)

  const modelParamsObj = {
    formBody: {
      style: userAnswers.style,
      gender: userAnswers.gender,
      attributes: { ...userAnswers.attributes },
      defaultVoice: null,
    },
  }

  const json = JSON.stringify(modelParamsObj)
  const base64 = btoa(unescape(encodeURIComponent(json)))

  return base64
}

// Add model params to URL
export const addModelParamsToUrl = (baseUrl, modelParams, additionalParams = {}) => {
  const url = new URL(baseUrl)

  // Add model-params
  url.searchParams.set('model-params', modelParams)

  // Add additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  // Add current URL params
  const currentUrlParams = new URLSearchParams(window.location.search)
  for (const [key, value] of currentUrlParams.entries()) {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  }

  return url.toString()
}

// UTM data management - now using our tracking system
export const collectAndStoreUTMData = () => {
  const rtkClickID = document.cookie.match(/rtkclickid-store=([^;]+)/)?.[1]

  // Initialize tracking if not already done
  if (typeof window !== 'undefined' && window.initTracking) {
    window.initTracking()
  }

  const trackingParams = getTrackingParams()

  // Also get current URL params directly as fallback
  const currentUrlParams = new URLSearchParams(window.location.search)
  const currentParams = {}
  for (const [key, value] of currentUrlParams.entries()) {
    currentParams[key] = value
  }
  const utmObj = {
    rtkcid: rtkClickID,
    click_id: rtkClickID,
    cmpid: rtkClickID,
    ...trackingParams,
    ...currentParams, // Include current URL params as fallback
    path: '/paywall',
  }

  // Store UTM data using our storage utility
  setStorage('utm_data', utmObj)
  return utmObj
}

// Enhanced auth callback handler with model params
export const handleAuthCallback = async (selections = null) => {
  const params = new URLSearchParams(window.location.search)
  const authToken = params.get('auth_token')
  const authType = params.get('auth_type')

  if (authToken && authType) {
    const utm = getStorage('utm_data') || {}
    const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`)

    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    paywallUrl.searchParams.set('auth_token', authToken)
    paywallUrl.searchParams.set('auth_type', authType)
    paywallUrl.searchParams.set('lp_source', LP_SOURCE)

    // Add type parameter based on selections style
    if (selections?.welcome) {
      const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
      paywallUrl.searchParams.set('type', typeValue)
    }

    // Add UTM params
    Object.entries(utm).forEach(([key, value]) => {
      if (value) paywallUrl.searchParams.set(key, value)
    })

    // Add model params if selections are available
    if (selections) {
      try {
        const modelParams = await createModelParams(selections)
        paywallUrl.searchParams.set('model-params', modelParams)
        // Model params added successfully
      } catch (error) {
        // Error adding model params
      }
    }

    window.location.replace(paywallUrl.toString())
    return true // Indicates callback was handled
  }
  return false // No callback to handle
}

// Account creation API with model params
export const createAccount = async (email, password, selections = null) => {
  const clickId = getRedTrackClickId()
  const payload = { email, password, clickId }

  const data = await apiRequest('/auth/account', 'POST', payload)

  if (data.success && data.redirectUrl && selections) {
    try {
      const modelParams = await createModelParams(selections)
      data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams)
      // Model params added successfully
    } catch (err) {
      // Error adding model params
    }
  }

  return data
}

// Enhanced pre-lander account creation API with model params
export const createPreLanderAccount = async (email, password, selections = null) => {
  const clickId = getRedTrackClickId()
  const utm = getStorage('utm_data') || {}
  const externalClickIds = await getExternalClickIds()

  const data = await apiRequest('/auth/pre-lander', 'POST', {
    email,
    password,
    clickId,
    ...utm,
    lp_source: LP_SOURCE,
    externalClickIds,
  })

  if (data.message && data.message.includes('User with this email already exists')) {
    return {
      success: false,
      error: 'Email already used',
    }
  }

  // If successful, add model params to any redirect URL
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams, { lp_source: LP_SOURCE })
  }

  return data
}

// Enhanced pre-lander login API with model params
export const loginPreLander = async (email, password, selections = null) => {
  const clickId = getRedTrackClickId()
  const utm = getStorage('utm_data') || {}

  const payload = {
    email,
    password,
    clickId,
    ...utm,
  }

  const data = await apiRequest('/auth/pre-lander', 'POST', payload)

  // Add model params to any redirect URL if selections are available
  if (data.success && data.redirectUrl && selections) {
    const modelParams = await createModelParams(selections)
    data.redirectUrl = addModelParamsToUrl(data.redirectUrl, modelParams, {
      auth_token: data.authToken,
      auth_type: 'user',
    })
    // Enhanced login redirect URL with model params
  }

  return data
}

// Enhanced complete login flow handler with model params
export const handleLoginFlow = async (email, password, selections) => {
  try {
    const clickId = getRedTrackClickId()
    const utm = getStorage('utm_data') || {}
    const externalClickIds = await getExternalClickIds()

    const data = await apiRequest('/auth/pre-lander/login', 'POST', {
      email,
      password,
      clickId,
      ...utm,
      lp_source: LP_SOURCE,
      externalClickIds,
    })

    const authToken = data.authToken || data.token
    const user = data

    if (authToken) {
      setAuth(authToken)

      const isSubscribed = user?.isSubscribed === true

      const isPw = window.location.pathname.includes('/pw')

      if (isSubscribed) {
        const homeUrl = new URL(`${SWIPEY_BASE_URL}/`)
        homeUrl.searchParams.set('auth_token', authToken)
        homeUrl.searchParams.set('auth_type', 'login_pre_lander')
        homeUrl.searchParams.set('lp_source', LP_SOURCE)

        Object.entries(utm).forEach(([key, value]) => {
          if (value) homeUrl.searchParams.set(key, value)
        })
        if (isPw) {
          homeUrl.searchParams.set('media', 'vid.mp4')
          homeUrl.searchParams.set('pw_v', '2')
        }

        return {
          success: true,
          redirectUrl: homeUrl.toString(),
        }
      } else {
        const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`)
        paywallUrl.searchParams.set('auth_token', authToken)
        paywallUrl.searchParams.set('auth_type', 'login_paywall')
        paywallUrl.searchParams.set('lp_source', LP_SOURCE)

        if (selections?.welcome) {
          const typeValue = selections.welcome === 'Anime' ? 'anime' : 'realistic'
          paywallUrl.searchParams.set('type', typeValue)
        }

        Object.entries(utm).forEach(([key, value]) => {
          if (value) paywallUrl.searchParams.set(key, value)
        })

        if (selections) {
          try {
            const modelParams = await createModelParams(selections)
            paywallUrl.searchParams.set('model-params', modelParams)
          } catch (err) {
            console.error('Error adding model-params', err)
          }
        }
        if (isPw) {
          paywallUrl.searchParams.set('media', 'vid.mp4')
          paywallUrl.searchParams.set('pw_v', '2')
        }

        return {
          success: true,
          redirectUrl: paywallUrl.toString(),
        }
      }
    }

    return {
      success: false,
      message: 'Please check your email or password',
    }
  } catch (error) {
    return {
      success: false,
      message: 'Please check your email or password',
    }
  }
}

// Enhanced Google auth URL with model params
// export const getGoogleAuthUrl = async (selections = null) => {
//   const baseUrl = `${SWIPEY_BASE_URL}/api/v1/auth/google/login`;

//   const clickId = getRedTrackClickId();
//   const utm = getStorage("utm_data") || {};

//   const stateObj = {
//     path: "/paywall",
//     click_id: clickId,
//     cmpid: clickId,
//     ...utm,
//   };

//   if (selections) {
//     const apiFormat = await convertSelectionsToApiFormat(selections);
//     const modelParamsObj = { formBody: apiFormat };
//     const base64ModelParams = btoa(
//       unescape(encodeURIComponent(JSON.stringify(modelParamsObj)))
//     );
//     stateObj["model-params"] = base64ModelParams;
//     stateObj.type = apiFormat.style === "Anime" ? "anime" : "realistic";
//   }

//   const url = new URL(baseUrl);
//   url.searchParams.set("state", JSON.stringify(stateObj));

//   return url.toString();
// };

export const getGoogleAuthUrl = () => `${SWIPEY_BASE_URL}/api/v1/auth/google/login`

// Swipey base URL
export const getSwipeyBaseUrl = () => SWIPEY_BASE_URL

// Export API configuration for debugging
export const getApiConfig = () => ({
  baseUrl: API_BASE_URL,
  swipeyBaseUrl: SWIPEY_BASE_URL,
  googleAuthUrl: getGoogleAuthUrl(),
  hasApiKey: !!API_KEY && API_KEY !== 'your-api-key-here',
  attributesLoaded,
  attributesMap,
})

// ---- Add to your API service file ----

// Request password reset (send code)
export const requestPasswordReset = async email => {
  return apiRequest('/auth/reset-password', 'POST', { email })
}

// Verify OTP for password reset
export const verifyPasswordOtp = async (email, otp) => {
  return apiRequest('/auth/verify-reset-password-otp', 'POST', { email, otp })
}

// Set new password using token
export const updatePassword = async (token, newPassword) => {
  return apiRequest(`/auth/set-new-password?token=${token}`, 'POST', {
    newPassword,
  })
}

// ---- Magic-link / email-only OTP flow (createsexfriends-gray-magicLink = ON) ----

const readCookie = name => {
  if (typeof document === 'undefined') return null
  const re = new RegExp(`(?:^|;\\s*)${name}=([^;]+)`)
  const match = document.cookie.match(re)
  return match ? decodeURIComponent(match[1]) : null
}

const getEfTransactionId = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const fromUrl = urlParams.get('ef_transaction_id')
    if (fromUrl) return fromUrl
  } catch (e) {
    /* ignore */
  }
  return readCookie('ef_transaction_id') || readCookie('transaction_id') || ''
}

// Decorate the backend-returned loginUrl with model-params, theme, and any
// /pw media params so the email-only flow lands on the same paywall as the
// password flow.
const decorateEmailOnlyRedirect = async (loginUrl, selections, ab_testing) => {
  if (!loginUrl) return null

  const url = new URL(loginUrl)

  if (selections?.welcome) {
    url.searchParams.set('type', selections.welcome === 'Anime' ? 'anime' : 'realistic')
  }

  if (selections) {
    try {
      const modelParams = await createModelParams(selections)
      url.searchParams.set('model-params', modelParams)
    } catch (err) {
      console.error('Error adding model-params to magic-link redirect', err)
    }
  }

  const theme = ab_testing?.['createsexfriends-paywall-theme']
  if (theme) url.searchParams.set('theme', theme)

  if (window.location.pathname.includes('/pw')) {
    url.searchParams.set('media', 'vid.mp4')
    url.searchParams.set('pw_v', '2')
  }

  // Forward the landing tracking params (twclid, gclid, utm_*, etc.) onto the paywall
  // redirect so magic-link matches the password flow. We read the stored utm_data
  // snapshot (the same source handleLoginFlow uses) — NOT window.location.search,
  // which SPA navigate({replace:true}) strips before submit.
  const utm = getStorage('utm_data') || {}
  Object.entries(utm).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  })
  // Click-ids (twclid, gclid, fbclid, ttclid) come from the landing-URL snapshot the
  // codebase trusts for them (utm_data is not a reliable source for click-ids).
  const forwardClickIds = await getExternalClickIds()
  Object.entries(forwardClickIds).forEach(([key, value]) => {
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value)
    }
  })

  return url.toString()
}

// POST /auth/pre-lander-with-email-only — used by both signup and login
// when the magic-link flag is ON. Returns either:
//   - otp_sent: true + otpExpiry  (existing email → caller shows OTP modal)
//   - authToken + loginUrl        (new email → caller navigates directly)
export const submitEmailOnly = async (email, { isResend = false, selections, ab_testing } = {}) => {
  const utm = getStorage('utm_data') || {}
  const ef_transaction_id = getEfTransactionId()
  const externalClickIds = await getExternalClickIds()

  const payload = {
    ...utm,
    email,
    ef_transaction_id,
    ab_testing,
    isResend,
    lp_source: LP_SOURCE,
    externalClickIds,
  }

  const data = await apiRequest('/auth/pre-lander-with-email-only', 'POST', payload)

  if (data?.success === false) return data

  if (data?.authToken && data?.loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(data.loginUrl, selections, ab_testing)
    if (redirectUrl) data.redirectUrl = redirectUrl
    if (data.authToken) setAuth(data.authToken)
  }

  return data
}

// POST /auth/pre-lander/login-with-otp — verifies the OTP and returns the
// authToken + loginUrl (same shape as the auto-login branch of submitEmailOnly).
export const verifyPreLanderOtp = async (email, otp, { selections, ab_testing } = {}) => {
  const data = await apiRequest('/auth/pre-lander/login-with-otp', 'POST', { email, otp })

  if (data?.success === false) return data

  const authToken = data?.authToken || data?.token
  const loginUrl = data?.loginUrl

  if (authToken) setAuth(authToken)

  if (loginUrl) {
    const redirectUrl = await decorateEmailOnlyRedirect(loginUrl, selections, ab_testing)
    if (redirectUrl) data.redirectUrl = redirectUrl
  }

  return data
}
